<template>
  <div class="ward-page">

    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" onclick="window.history.back()"><i class="ft-arrow-left"></i></button>
      <div class="header-info">
        <span class="page-title">Review / Edit Orders</span>
        <span class="page-subtitle"><i class="ft-calendar"></i> {{ date }}</span>
      </div>
    </div>

    <!-- Step 1: Ward / Floor selection -->
    <div class="section-pad">
      <div class="step-label">
        <div class="step-badge">1</div>
        <span>Select a ward</span>
      </div>

      <!-- Floor tabs -->
      <div class="floor-tabs" v-if="merchant.use_floors == 'Y' && floors.length > 0">
        <button
          class="floor-tab"
          :class="{ 'floor-tab--active': selectedFloorId === floor.floor_id }"
          v-for="floor in floors"
          :key="floor.floor_id"
          @click="selectFloor(floor)"
        >
          <i class="ft-layers"></i> {{ floor.floor_name }}
        </button>
      </div>

      <!-- Ward grid with scroll arrows -->
      <div class="ward-scroll-wrap" v-if="wards.length > 0">
        <button
          class="scroll-arrow scroll-arrow--left"
          @click="scrollWards(-1)"
          v-show="canScrollLeft"
          aria-label="Scroll left"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>

        <div class="ward-grid" ref="wardGrid" @scroll="updateArrows">
          <button
            class="ward-card"
            :class="{ 'ward-card--active': selectedWardId === w.ward_id }"
            v-for="w in wards"
            :key="w.ward_id"
            @click="selectWard(w)"
          >
            <div class="ward-card-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <span class="ward-card-name">{{ w.ward_name }}</span>
            <div class="ward-card-check" v-if="selectedWardId === w.ward_id">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </button>
        </div>

        <button
          class="scroll-arrow scroll-arrow--right"
          @click="scrollWards(1)"
          v-show="canScrollRight"
          aria-label="Scroll right"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>

      <div class="no-wards-hint" v-if="wards.length === 0 && merchant.use_floors == 'Y'">
        <i class="ft-info"></i> Select a floor to see wards.
      </div>
    </div>

    <!-- Loading -->
    <div class="loading-state" v-if="loadingBeds">
      <div class="spinner"></div>
      <span>Loading patients…</span>
    </div>

    <!-- Step 2: search + count -->
    <div class="section-pad" v-if="!loadingBeds && beds.length > 0">
      <div class="ward-summary">
        <div class="ward-summary-info">
          <div class="step-label" style="margin-bottom:0">
            <div class="step-badge">2</div>
            <span>Select a patient</span>
          </div>
          <span class="bed-count-badge">{{ filteredBeds.length }} patient{{ filteredBeds.length !== 1 ? 's' : '' }}</span>
        </div>
        <div class="search-wrap">
          <i class="ft-search search-icon"></i>
          <input
            class="search-input"
            type="text"
            v-model="searchQuery"
            placeholder="Search by name or bed…"
          >
          <button class="search-clear" v-if="searchQuery" @click="searchQuery = ''">
            <i class="ft-x"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div class="empty-state" v-if="!loadingBeds && beds.length > 0 && filteredBeds.length === 0">
      <i class="ft-search"></i>
      <p>No patients match <strong>"{{ searchQuery }}"</strong></p>
      <button class="clear-search-btn" @click="searchQuery = ''">Clear search</button>
    </div>

    <!-- Empty ward -->
    <div class="empty-state" v-if="!loadingBeds && beds.length === 0 && selectedWardId !== ''">
      <i class="ft-inbox"></i>
      <p>No patients found in this ward.</p>
    </div>

    <!-- Bed cards -->
    <div class="section-pad beds-section" v-if="!loadingBeds && filteredBeds.length > 0">
      <div class="beds-grid">
        <div class="patient-block" v-for="bed in filteredBeds" :key="bed.bed_id">
          <a href="javascript:void(0)" class="bed-card" @click="openBed(bed)">

            <div class="bed-card-top">
              <div class="patient-avatar">{{ bed.patient_name ? bed.patient_name.charAt(0).toUpperCase() : '?' }}</div>
              <div class="bed-info">
                <div class="bed-number">Bed {{ bed.bed_code }}</div>
                <div class="patient-name">{{ bed.patient_name }}</div>
              </div>
            </div>

            <div class="bed-card-meta">
              <span class="menu-pill"><i class="ft-bookmark"></i> {{ bed.menu_type_name }}</span>
              <span class="allergy-pill" v-if="bed.allergies && bed.allergies != ''">
                <i class="ft-alert-triangle"></i> {{ allergies(bed.allergies) }}
              </span>
            </div>

            <div class="important-info" v-if="bed.important_info != null && bed.important_info != ''">
              <i class="ft-alert-circle"></i> {{ bed.important_info }}
            </div>

            <div class="order-breakdown">
              <div class="breakdown-col">
                <div class="breakdown-day">Today</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.today.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.today.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.today.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.today.breakfast && !bed.order_breakdown.today.lunch && !bed.order_breakdown.today.dinner">—</span>
                </div>
              </div>
              <div class="breakdown-divider"></div>
              <div class="breakdown-col">
                <div class="breakdown-day">Tomorrow</div>
                <div class="breakdown-dots">
                  <span class="meal-dot dot-b" v-if="bed.order_breakdown.tomorrow.breakfast">B</span>
                  <span class="meal-dot dot-l" v-if="bed.order_breakdown.tomorrow.lunch">L</span>
                  <span class="meal-dot dot-d" v-if="bed.order_breakdown.tomorrow.dinner">D</span>
                  <span class="meal-none" v-if="!bed.order_breakdown.tomorrow.breakfast && !bed.order_breakdown.tomorrow.lunch && !bed.order_breakdown.tomorrow.dinner">—</span>
                </div>
              </div>
            </div>

            <div class="review-cta">
              <i class="ft-edit-2"></i> Review / Edit Orders
            </div>

          </a>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment'
import swal from 'sweetalert'

export default {
  name: 'ward',
  data: function () {
    var user = JSON.parse(window.localStorage.getItem('user'))
    return {
      merchant: user.merchant,
      floors: user.merchant.floors || [],
      date: moment().format('DD MMMM YYYY'),
      wards: user.merchant.use_floors === 'Y' ? [] : user.merchant.wards,
      beds: [],
      searchQuery: '',
      loadingBeds: false,
      selectedWardId: '',
      selectedFloorId: '',
      canScrollLeft: false,
      canScrollRight: false
    }
  },
  computed: {
    filteredBeds: function () {
      var q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.beds
      return this.beds.filter(function (b) {
        return (b.patient_name || '').toLowerCase().includes(q) ||
               (b.bed_code || '').toLowerCase().includes(q)
      })
    }
  },
  mounted: function () {
    this.$store.commit('SET_LAYOUT', 'AppLayout')
    this.$nextTick(this.updateArrows)
  },
  updated: function () {
    this.$nextTick(this.updateArrows)
  },
  methods: {
    scrollWards: function (dir) {
      var el = this.$refs.wardGrid
      if (!el) return
      el.scrollBy({ left: dir * 240, behavior: 'smooth' })
    },
    updateArrows: function () {
      var el = this.$refs.wardGrid
      if (!el) return
      this.canScrollLeft  = el.scrollLeft > 4
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4
    },
    selectFloor: function (floor) {
      this.selectedFloorId = floor.floor_id
      this.wards = floor.wards
      this.beds = []
      this.selectedWardId = ''
      this.searchQuery = ''
    },
    selectWard: function (w) {
      this.selectedWardId = w.ward_id
      this.searchQuery = ''
      this.beds = []
      this.loadBeds(w.ward_id)
    },
    loadBeds: function (ward_id) {
      var that = this
      var user = JSON.parse(window.localStorage.getItem('user'))
      that.loadingBeds = true

      $.ajax({
        beforeSend: function (xhr) {
          xhr.setRequestHeader('Authorization', window.localStorage.getItem('token'))
        },
        url: that.$config.apiUrl + 'medicater/beds/' + ward_id + '?date=' + moment().format('YYYY-MM-DD') + '&ward_id=' + ward_id,
        type: 'get',
        success: function (data) {
          that.loadingBeds = false
          that.beds = data.data || []
        },
        error: function (err) {
          that.loadingBeds = false
          var cached = window.localStorage.getItem(this.url)
          if (cached) {
            try { that.beds = JSON.parse(cached).data || [] } catch (e) { that.beds = [] }
          } else {
            swal({ text: 'Could not load patients. Please check your connection.', icon: 'info', button: 'Ok' })
          }
        }
      })
    },
    allergies: function (bedAllergies) {
      var out = ''
      bedAllergies.forEach(function (a) { out += a.name + ', ' })
      return out.substring(0, out.length - 2)
    },
    openBed: function (bed) {
      window.localStorage.setItem('menu_type_id', bed.menu_type_id)
      window.localStorage.setItem('bed', JSON.stringify(bed))
      this.$router.push('/preorder/' + bed.ward_id + '/' + bed.bed_id)
    }
  }
}
</script>

<style scoped>
.ward-page {
  min-height: 100vh;
  background: #f0f4f8;
  padding-bottom: 40px;
}

/* ── Page header ── */
.page-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 30px 20px 20px;
  background: linear-gradient(135deg, #1a587b 0%, #0f3d5c 100%);
  position: fixed;
  top: 0; left: 0;
  width: 100%;
  z-index: 10;
  margin-top: 3.2rem;
  box-shadow: 0 8px 32px rgba(15, 61, 92, 0.35);
}

.back-btn {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.2);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; cursor: pointer; flex-shrink: 0;
  transition: background 0.15s;
}
.back-btn:hover { background: rgba(255,255,255,0.25); }

.header-info { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
.page-title   { font-size: 1.05rem; font-weight: 700; color: #fff; }
.page-subtitle {
  font-size: 0.73rem; color: rgba(255,255,255,0.65); font-weight: 500;
  display: flex; align-items: center; gap: 4px;
}
.page-subtitle i { font-size: 0.65rem; }

/* ── Section padding ── */
.section-pad {   margin: 0 auto; }
.section-pad + .section-pad { padding-top: 16px; }
.beds-section { padding-top: 16px; }

/* ── Step label ── */
.step-label {
  display: flex; align-items: center; gap: 9px;
  margin-bottom: 12px;
}
.step-badge {
  width: 24px; height: 24px; border-radius: 50%;
  background: #1a587b; color: #fff;
  font-size: 0.72rem; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.step-label span {
  font-size: 0.75rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 1.2px; color: #64748b;
}

/* ── Floor tabs ── */
.floor-tabs { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.floor-tab {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 999px;
  background: #fff; border: 1.5px solid #e8edf2;
  font-size: 0.82rem; font-weight: 600; color: #334155;
  cursor: pointer; transition: all 0.15s;
}
.floor-tab:hover { border-color: #1a587b; color: #1a587b; }
.floor-tab--active { background: #1a587b; border-color: #1a587b; color: #fff; }

/* ── Ward scroll wrapper ── */
.ward-scroll-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
}

.scroll-arrow {
  flex-shrink: 0;
  width: 32px; height: 32px;
  border-radius: 50%;
  border: 1.5px solid #e2e8f0;
  background: #ef5953;
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transition: background 0.15s, border-color 0.15s, transform 0.1s;
  z-index: 1;
}
.scroll-arrow:hover  { background: #e0f2fe; border-color: #1a587b; }
.scroll-arrow:active { transform: scale(0.93); }

/* ── Ward grid ── */
.ward-grid {
  display: flex;
  flex-direction: row;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 6px;
  margin-bottom: 2px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  padding: 0.5em;
}
.ward-grid::-webkit-scrollbar { display: none; }

.ward-card {
  position: relative;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 16px 12px;
  background: #fff; border-radius: 14px;
  border: 1.5px solid #e8edf2;
  box-shadow: 0 -3px 8px rgba(18,69,100,0.18), 0 5px 14px rgba(18,69,100,0.22);
  cursor: pointer; text-align: center;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
  font-size: 0.82rem; font-weight: 600; color: #334155;
  flex: 0 0 110px;
  min-width: 110px;
}
.ward-card:hover { border-color: #1a587b; box-shadow: 0 -4px 10px rgba(18,69,100,0.22), 0 7px 18px rgba(18,69,100,0.28); }
.ward-card:active { transform: scale(0.97); }
.ward-card--active { border-color: #1a587b; background: #f0f9ff; color: #1a587b; }

.ward-card-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: #e0f2fe; color: #1a587b;
  display: flex; align-items: center; justify-content: center;
}
.ward-card--active .ward-card-icon { background: #1a587b; color: #fff; }
.ward-card-name { line-height: 1.3; }
.ward-card-check {
  position: absolute; top: 7px; right: 7px;
  width: 20px; height: 20px; border-radius: 50%;
  background: #1a587b; color: #fff;
  display: flex; align-items: center; justify-content: center;
}

.no-wards-hint {
  font-size: 0.82rem; color: #94a3b8;
  display: flex; align-items: center; gap: 6px;
  padding: 10px 0;
}

/* ── Loading ── */
.loading-state {
  display: flex; flex-direction: column; align-items: center;
  gap: 14px; padding: 50px 16px;
  color: #94a3b8; font-size: 0.875rem;
}
.spinner {
  position: relative; width: 38px; height: 38px; border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #1a587b; border-left-color: #1a587b;
  animation: spin 0.9s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;
}
.spinner::after {
  content: ''; position: absolute; inset: 5px; border-radius: 50%;
  border: 2.5px solid transparent;
  border-top-color: rgba(26,88,123,0.35); border-left-color: rgba(26,88,123,0.35);
  animation: spin 1.8s cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite reverse;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Step 2: summary + search ── */
.ward-summary { display: flex; flex-direction: column; gap: 10px; }
.ward-summary-info {
  display: flex; align-items: center; justify-content: space-between;
}
.bed-count-badge {
  font-size: 0.72rem; font-weight: 700;
  background: #e0f2fe; color: #1a587b;
  padding: 3px 10px; border-radius: 999px;
}
.search-wrap { position: relative; display: flex; align-items: center; }
.search-icon {
  position: absolute; left: 13px; color: #94a3b8;
  font-size: 0.875rem; pointer-events: none;
}
.search-input {
  width: 100%; padding: 11px 38px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-size: 0.875rem; color: #0f172a; background: #fff;
  outline: none; box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.search-input:focus { border-color: #1a587b; box-shadow: 0 0 0 3px rgba(26,88,123,0.1); }
.search-input::placeholder { color: #b0bcc8; }
.search-clear {
  position: absolute; right: 10px;
  width: 22px; height: 22px; border-radius: 50%;
  border: none; background: #e2e8f0; color: #64748b;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.65rem; cursor: pointer;
}

/* ── Empty state ── */
.empty-state {
  display: flex; flex-direction: column; align-items: center;
  gap: 10px; padding: 50px 24px; text-align: center; color: #94a3b8;
}
.empty-state i { font-size: 2rem; }
.empty-state p { font-size: 0.875rem; color: #64748b; margin: 0; }
.clear-search-btn {
  padding: 8px 18px; border-radius: 8px;
  border: 1.5px solid #e2e8f0; background: #fff;
  color: #1a587b; font-size: 0.82rem; font-weight: 600; cursor: pointer;
}

/* ── Beds grid ── */
.beds-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.patient-block { display: contents; }

/* ── Bed card ── */
.bed-card {
  display: flex; flex-direction: column; height: 100%;
  background: #fff; border-radius: 14px;
  border: 2px solid #e8edf2;
  box-shadow: 0 4px 16px rgba(0,0,0,0.14), 0 1px 4px rgba(0,0,0,0.10);
  text-decoration: none !important;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.15s;
}
.bed-card:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12); transform: translateY(-2px); }
.bed-card:active { transform: scale(0.98); }

.bed-card-top {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 14px 10px;
}
.patient-avatar {
  width: 36px; height: 36px; border-radius: 10px;
  background: linear-gradient(135deg, #1a587b, #0f3d5c);
  display: flex; align-items: center; justify-content: center;
  font-size: 0.95rem; font-weight: 700; color: #fff; flex-shrink: 0;
}
.bed-info { flex: 1; min-width: 0; }
.bed-number {
  font-size: 0.68rem; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.8px;
}
.patient-name {
  font-size: 0.875rem; font-weight: 700; color: #0f172a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.bed-card-meta { display: flex; flex-wrap: wrap; gap: 5px; padding: 0 14px 10px; }
.menu-pill, .allergy-pill {
  font-size: 0.67rem; font-weight: 600;
  padding: 3px 8px; border-radius: 999px;
  display: flex; align-items: center; gap: 4px;
}
.menu-pill    { background: #e0f2fe; color: #1a587b; }
.allergy-pill { background: #fff7ed; color: #c2410c; }

.important-info {
  margin: 0 14px 10px; padding: 8px 10px;
  background: #fffbeb; border: 1px solid #fde68a;
  border-radius: 8px; font-size: 0.73rem; color: #92400e;
  display: flex; align-items: center; gap: 6px; line-height: 1.4;
}

/* ── Meal breakdown ── */
.order-breakdown {
  display: flex; align-items: stretch;
  border-top: 1px solid #999a9b;
  background: #f8fafc;
}
.breakdown-col {
  flex: 1; padding: 10px 12px;
  display: flex; flex-direction: column; gap: 6px;
}
.breakdown-divider { width: 1px; background: #999a9b; margin: 8px 0; }
.breakdown-day {
  font-size: 0.62rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.8px; color: #94a3b8;
}
.breakdown-dots { display: flex; gap: 4px; flex-wrap: wrap; }
.meal-dot {
  width: 20px; height: 20px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.6rem; font-weight: 700; color: #fff;
}
.dot-b { background: #0ea5e9; }
.dot-l { background: #10b981; }
.dot-d { background: #8b5cf6; }
.meal-none { font-size: 0.75rem; color: #cbd5e1; font-weight: 600; }

/* ── Review CTA ── */
.review-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  background: #124564;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  border-radius: 15px;
  margin-top: auto;
  margin-bottom: 1em;
  max-width: 90%;
  align-self: center;
  width: 100%;
  transition: opacity 0.15s;
}
.bed-card:hover .review-cta { opacity: 0.88; }
</style>
